import RenderOnViewportEntry from '@creator/ui/components/RenderOnViewportEntry/RenderOnViewportEntry';
import { ReactLazyPreload } from '@creator/ui/utils/react';
import { cn } from '@creator/ui/utils/ui';
import AdvertisingSlot from '@src/components/Ads/AdvertisingSlot';
import DistroScale from '@src/components/DistroScale/DistroScale';
import CreateFeedItemInput from '@src/components/Token/CreateFeedItemInput/CreateFeedItemInput';
import PartnerList from '@src/components/Token/PartnerList/PartnerList';
import { shouldDisplayAds } from '@src/model/config/helpers';
import { useStoreState } from '@src/model/hooks';
import { getUserId, isUserWhitelisted } from '@src/model/user/helpers';
import storageService from '@src/services/storage-service';
import { useMainPostId } from '@src/store/posts/hooks/useMainPost';
import { storeEqualityFn } from '@src/utils/object-utils/object-utils';
import { type FC, memo, useLayoutEffect } from 'react';
import BottomStickyAd from '../BottomStickyAd';
import PartnerPosts from '../PartnerPosts';
import PostsTabHero from './PostsTabHero';
import TopicsBar from './TopicsBar';

const CommunitySection = ReactLazyPreload(() => import('./CommunitySection'));

export interface PostsTabProps {
    tokenName: string;
    className?: string;
    flairId?: string;
}

const TokenPagePostsTab: FC<PostsTabProps> = props => {
    const { tokenName, flairId, className = '' } = props;

    const mainPostId = useMainPostId({ tokenName, flairId }) || '';
    const isLoggedIn = useStoreState(state => state.user.isLoggedIn);
    const token = useStoreState(state => state.token.getToken(tokenName), storeEqualityFn);

    useLayoutEffect(() => {
        CommunitySection.preload();

        return () => {
            storageService.memoryStorage.set(`${tokenName}_postListScrollPosition`, window.scrollY.toString());
        };
    }, []);

    function renderCreateFeedItemInput() {
        if (!isLoggedIn || !isUserWhitelisted(getUserId(), tokenName)) return null;

        return <CreateFeedItemInput tokenName={tokenName} postKind="story" className="mx-4 mb-4" />;
    }

    function renderPartnerPosts() {
        if (!token) return null;
        return <PartnerPosts excludePostId={mainPostId} tokenName={tokenName} flairId={flairId} />;
    }

    function renderCommunitySection() {
        if (!token) return null;
        return <CommunitySection tokenName={tokenName} flairId={flairId} />;
    }

    function renderTopCenterAd() {
        if (!token) return null;
        return <AdvertisingSlot className="mb-4" location="top_center" tokenName={tokenName} />;
    }

    function renderDistroScale() {
        if (!token) return null;
        if (!shouldDisplayAds(tokenName)) return null;

        return <DistroScale className="" />;
    }

    return (
        <div className={cn('', className)}>
            <PostsTabHero tokenName={tokenName} postId={mainPostId} className="" />
            <PartnerList tokenName={tokenName} />
            {renderTopCenterAd()}
            {renderCreateFeedItemInput()}
            <TopicsBar preventScrollToTop={true} className="" tokenName={tokenName} flairId={flairId} />
            {renderDistroScale()}
            {renderPartnerPosts()}
            <div className="bg-[#F6F7FF] dark:bg-gray-800">
                <RenderOnViewportEntry>{renderCommunitySection()}</RenderOnViewportEntry>
                <BottomStickyAd tokenName={tokenName} />
            </div>
        </div>
    );
};

export default memo(TokenPagePostsTab);
